import React, { ReactNode } from 'react';
import { CheckCircle } from 'lucide-react';

export interface FormModalProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  onSubmit: (data?: any) => void | Promise<void>;
  onCancel: () => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
  formId?: string;
  showSubmitButton?: boolean;
  useFormData?: boolean;
}

const FormModal: React.FC<FormModalProps> = ({
  title,
  subtitle,
  icon,
  children,
  onSubmit,
  onCancel,
  submitText = 'Save',
  cancelText = 'Cancel',
  loading = false,
  size = 'medium',
  showSubmitButton = true,
  useFormData = true,
}) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (useFormData) {
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());
      await onSubmit(data);
    } else {
      await onSubmit();
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6 rounded-t-2xl">
        <div className="flex items-center gap-4">
          {icon && (
            <div className="p-3 bg-white/10 rounded-xl">
              {icon}
            </div>
          )}
          <div>
            <h3 className="text-2xl font-bold text-white">{title}</h3>
            {subtitle && <p className="text-slate-300 text-sm mt-1">{subtitle}</p>}
          </div>
        </div>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit}>
        <div className={`bg-white ${size === 'large' ? 'px-8 py-8' : size === 'small' ? 'px-6 py-5' : 'px-8 py-6'}`}>
          <div className="space-y-5">
            {children}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-slate-50 rounded-b-2xl border-t border-slate-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-5 py-2.5 bg-white text-slate-700 font-medium rounded-xl border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all disabled:opacity-50 text-sm"
          >
            {cancelText}
          </button>
          {showSubmitButton && (
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm disabled:opacity-50 text-sm"
            >
              <CheckCircle size={16} />
              {submitText}
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default FormModal;
